/**
 * The floor-type catalogue — the single source of truth for what floor
 * types exist is `floors.store` (repo root); this module imports its
 * generated projection `floors.data.ts` (do not edit — run
 * `node tools/floor-store.js` or save in the knobs app's Floors tab).
 *
 * A floor type is a RECIPE, not a dungeon: a custom name + the NPC kinds
 * that may spawn in normal rooms (`roomNpcs`) + the numeric boss IDs that may be
 * the boss (`bossIds`) + an attached room type (`roomId`, rooms.store).
 * The dungeon itself (layout, doors, keys) is still seed-generated (gen/)
 * — the recipe decides WHO spawns and HOW rooms are furnished (the
 * generator furnishes every non-base room with the room type's obstacle
 * rules — sim/furnish.ts). `roomId: null` = legacy bare rooms (no
 * furnishing, zero RNG draws). The sim consumes resolved pools (sim/state.ts
 * Spawns); the lobby's select and the server's room recipe (net/room.ts)
 * resolve kinds + the room recipe from HERE.
 *
 * Room kinds reference `npc.store`; boss IDs reference `bosses.store`.
 * References are enforced twice — at store save and HERE at module load
 * (a dangling kind or ID is a
 * build error, never a runtime surprise — e.g. when an NPC is deleted in
 * the knobs app's NPCs tab while a floor still references it).
 *
 * The committed catalog may be EMPTY (the user authors floor types through
 * the editor): the lobby then shows "no floors available" and room
 * creation is disabled, while the headless no-floor path (play, agents)
 * uses all ordinary NPCs for rooms and the boss catalog for bosses.
 */
import { FLOORS_DATA } from "./floors.data.js";
import { BOSSES } from "./bosses.js";
import { NPCS } from "./npcs.js";
import { ROOMS } from "./rooms.js";
import { LOOT } from "./loot.js";
import { readPuzzleRoomPct, validatePuzzleSetups } from "./puzzles.js";
import type { PuzzlePlacement } from "./puzzles.js";

export interface FloorTypeDef extends PuzzlePlacement {
  /** Permanent store id (floors.store — never renumbered, never reused;
   *  the catalog may have gaps after deletions). Carried on Hello.floor. */
  id: number;
  /** Free-form display name (the lobby's select, the server console line). */
  name: string;
  /** npc.store kinds that may spawn in normal rooms (0–5 per room, one
   *  uniform kind draw per enemy — sim/state.ts). Empty = mob-free rooms. */
  roomNpcs: string[];
  /** bosses.store IDs that may be the floor's boss (exactly one, drawn
   *  uniformly at random). Empty = no boss monster (the exit portal is
   *  open from the start — bossEnemyId null). */
  bossIds: number[];
  /** The attached room type (rooms.store id, the `rooms` editor) — the
   *  generator furnishes every non-base room with its recipe. null =
   *  legacy bare rooms (no furnishing at all). */
  roomId: number | null;
  /** The base-room SACK table (a loot.store id, design/loot.md): the
   *  floor's one sack stands in the base room, its contents rolled when
   *  the floor is generated. null = no sack in that floor's base room.
   *  Consumed by the sim's generation (loot.md build 4). */
  sackTable: number | null;
  /** Guardian doors (design/guardian-door.md): percent (0–100) of eligible
   *  rooms sealed by a guardian door on their open child edges. 0 = off
   *  (the default — no guardian doors, no extra mapgen draw). */
  guardianRoomPct: number;
}

/**
 * All floor types, validated at load with the same discipline the other
 * catalogs use (unique ids, non-empty name, legal kinds, legal references:
 * a bad store is a build error, never a runtime surprise). `lootIds`
 * (optional) is the set of allowed sack-table ids — tests pass an explicit
 * set (a unit test never depends on what a human authored); the default is
 * the committed LOOT.
 */
export function buildFloorCatalog(data: readonly unknown[] = FLOORS_DATA, lootIds?: ReadonlySet<number>, allowedBossIds?: ReadonlySet<number>): readonly FloorTypeDef[] {
  const ids = new Set<number>();
  const npcKinds = new Set(NPCS.map((n) => n.kind));
  const roomIds = new Set(ROOMS.map((r) => r.id));
  const tableIds = lootIds ?? new Set(LOOT.map((t) => t.id));
  const out: FloorTypeDef[] = [];
  // Widen the `as const` literal types: the generated data is validated
  // here at runtime (a hand-edited floors.data.ts must fail loudly, not
  // type-trick its way past the checks). `data` is injectable (tests) —
  // the default is the committed generated module.
  for (const r of data) {
    const raw = r as {
      id?: unknown; name?: unknown; roomNpcs?: unknown; bossIds?: unknown;
      roomId?: unknown; sackTable?: unknown; guardianRoomPct?: unknown; puzzleRoomPct?: unknown; puzzles?: unknown;
    };
    if (!Number.isInteger(raw.id) || (raw.id as number) < 0) {
      throw new Error(`floors.data: bad id ${String(raw.id)} (${String(raw.name)})`);
    }
    const id = raw.id as number;
    if (ids.has(id)) throw new Error(`floors.data: duplicate id ${id} (${String(raw.name)})`);
    ids.add(id);
    if (typeof raw.name !== "string" || raw.name === "") {
      throw new Error(`floors.data: id ${id}: missing name`);
    }
    const bossIds = allowedBossIds ?? new Set(BOSSES.map((b) => b.id));
    if (!Array.isArray(raw.bossIds) || !raw.bossIds.every((id) => Number.isSafeInteger(id) && id >= 0)) throw new Error(`floors.data: id ${id}: bossIds must be an array of boss IDs`);
    if (new Set(raw.bossIds).size !== raw.bossIds.length) throw new Error(`floors.data: id ${id}: duplicate boss ID`);
    for (const bossId of raw.bossIds) if (!bossIds.has(bossId)) throw new Error(`floors.data: id ${id}: boss ${bossId} is not in bosses.store`);
    const pools: { label: string; kinds: string[] }[] = [];
    for (const [label, value] of [["roomNpcs", raw.roomNpcs]] as const) {
      if (!Array.isArray(value) || !value.every((k) => typeof k === "string" && k !== "")) {
        throw new Error(`floors.data: id ${id}: ${label} must be an array of npc kinds`);
      }
      const kinds = value as string[];
      const seen = new Set<string>();
      for (const k of kinds) {
        if (seen.has(k)) throw new Error(`floors.data: id ${id}: duplicate kind ${k} in ${label}`);
        seen.add(k);
        if (!npcKinds.has(k)) {
          throw new Error(`floors.data: id ${id}: kind ${k} is not in the NPC catalog (npc.store) — fix the reference or the store`);
        }
      }
      pools.push({ label, kinds });
    }
    // The attached room type (optional): a dangling id is a build error,
    // the same discipline as the NPC kind cross-checks above. THREE encodings
    // of "no room" are all legal and must all survive: the store file omits
    // the line, the data module writes an EXPLICIT null (the emitter's
    // choice for null), and a hand-written module may omit the field. (The
    // explicit null used to crash every server build — docs/traps/store-drift.md.)
    let roomId: number | null = null;
    if (raw.roomId !== undefined && raw.roomId !== null) {
      if (!Number.isInteger(raw.roomId) || (raw.roomId as number) < 0) {
        throw new Error(`floors.data: id ${id}: roomId must be a non-negative integer or null (got ${String(raw.roomId)})`);
      }
      roomId = raw.roomId as number;
      if (!roomIds.has(roomId)) {
        throw new Error(`floors.data: id ${id}: room ${roomId} is not in the room catalog (rooms.store) — fix the reference or the store`);
      }
    }
    // The sack-table reference: the same three encodings of "no sack" as
    // roomId (absent / explicit null / omitted) and the same double-check
    // discipline (store save + HERE).
    let sackTable: number | null = null;
    if (raw.sackTable !== undefined && raw.sackTable !== null) {
      if (!Number.isInteger(raw.sackTable) || (raw.sackTable as number) < 0) {
        throw new Error(`floors.data: id ${id}: sackTable must be a non-negative integer or null (got ${String(raw.sackTable)})`);
      }
      sackTable = raw.sackTable as number;
      if (!tableIds.has(sackTable)) {
        throw new Error(`floors.data: id ${id}: sack table ${sackTable} is not in the loot catalog (loot.store) — fix the reference or the store`);
      }
    }
    // The guardian-door percentage (design/guardian-door.md): 0 when the
    // store omits the line (the pre-feature encoding — the live store has
    // no such lines until a human adds them); an integer 0–100 when present.
    let guardianRoomPct = 0;
    if (raw.guardianRoomPct !== undefined && raw.guardianRoomPct !== null) {
      if (!Number.isInteger(raw.guardianRoomPct) || (raw.guardianRoomPct as number) < 0 || (raw.guardianRoomPct as number) > 100) {
        throw new Error(`floors.data: id ${id}: guardianRoomPct must be an integer 0–100 (got ${String(raw.guardianRoomPct)})`);
      }
      guardianRoomPct = raw.guardianRoomPct as number;
    }
    const puzzles = validatePuzzleSetups(raw.puzzles);
    for (const p of puzzles) if (p.kind === "orb-wizard" && !bossIds.has(p.bossId)) throw new Error(`floors.data: wizard boss ${p.bossId} is not in bosses.store`);
    if (!allowedBossIds) for (const p of puzzles) if (p.kind === "orb-wizard" && BOSSES.find((b) => b.id === p.bossId)?.mechanic.kind !== "orb-burst") throw new Error(`floors.data: wizard boss ${p.bossId} must use orb-burst`);
    out.push({
      id,
      name: raw.name,
      roomNpcs: pools[0]!.kinds,
      bossIds: raw.bossIds as number[],
      roomId,
      sackTable,
      guardianRoomPct,
      puzzleRoomPct: readPuzzleRoomPct(raw.puzzleRoomPct),
      puzzles,
    });
  }
  return out;
}

/** All floor types, by id (may have gaps — permanent wire ids). */
export const FLOORS: readonly FloorTypeDef[] = buildFloorCatalog();

/** Catalog lookup; undefined for unknown ids (the server rejects those). */
export function floorById(id: number): FloorTypeDef | undefined {
  return FLOORS.find((f) => f.id === id);
}
